"use client";

import { useRef } from "react";
import { Loader2, RefreshCw } from "@/components/icons";
import type { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { useBuyAgain } from "@/hooks/checkout/use-buy-again";
import { useHydrateOnce } from "@/hooks/use-hydrate-once";
import { cn } from "@/lib/utils";
import { BuyAgainDialog } from "./buy-again-dialog";

interface BuyAgainButtonProps {
  orderId: Id<"orders">;
  productIds?: Id<"products">[];
  variant?: "primary" | "outline";
  size?: "default" | "sm";
  className?: string;
  highlight?: boolean;
}

export function BuyAgainButton({
  orderId,
  productIds,
  variant = "primary",
  size = "default",
  className,
  highlight = false,
}: BuyAgainButtonProps) {
  const buyAgain = useBuyAgain({ orderId, productIds });
  const buttonRef = useRef<HTMLButtonElement>(null);
  useHydrateOnce(highlight ? orderId : null, () => {
    buttonRef.current?.scrollIntoView({ block: "center" });
    buttonRef.current?.focus({ preventScroll: true });
  });

  return (
    <>
      <Button
        ref={buttonRef}
        type="button"
        variant={variant}
        size={size}
        className={cn(highlight && "ring-2 ring-ring ring-offset-2", className)}
        disabled={buyAgain.isWorking || buyAgain.isLoading}
        onClick={() => void buyAgain.start()}
      >
        {buyAgain.isWorking ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <RefreshCw className="size-4" />
        )}
        Buy again
      </Button>
      <BuyAgainDialog
        review={buyAgain.review}
        isWorking={buyAgain.isWorking}
        onContinue={(keep) => void buyAgain.confirm(keep)}
        onClose={buyAgain.dismissReview}
      />
    </>
  );
}
